// ~/avaai_dashboard/frontend/src/hooks/useCameraFaceTracking.ts
import { useEffect, useRef, useState } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { uploadVisionFrame, } from "../services/avaVisionUploader";
import { setLatestVision } from "../services/avaVisionContext";
// =====================================================
// AVA REAL-TIME VISION LOOP (STABLE FIX)
// =====================================================
let avaVisionInterval = null;
let lastVisionUpload = 0;
const VISION_UPLOAD_INTERVAL = 1000;
async function sendFrameToVision(captureFrame, faceEvidence) {
    try {
        const frame = captureFrame();
        if (!frame) {
            return;
        }
        console.log("[AVA VISION FRAME]", {
            characters: frame.length,
            payloadLogged: false,
        });
        const result = await uploadVisionFrame(frame, faceEvidence);
        if (result) {
            console.log("[AVA VISION]", {
                objects: result.objects?.length ?? 0,
                scene: result.scene,
                text: result.text?.length ?? 0,
            });
            setLatestVision(result);
        }
    }
    catch (e) {
        console.warn("[AVA REALTIME VISION ERROR]", e);
    }
}
const WASM_BASE_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm";
const MODEL_URL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
}
// Some browsers require camera start after user gesture
function ensureUserGesture() {
    return new Promise((resolve) => {
        let done = false;
        const finish = () => {
            if (done)
                return;
            done = true;
            window.removeEventListener("pointerdown", finish);
            window.removeEventListener("keydown", finish);
            window.removeEventListener("touchstart", finish);
            resolve();
        };
        window.addEventListener("pointerdown", finish, { once: true });
        window.addEventListener("keydown", finish, { once: true });
        window.addEventListener("touchstart", finish, { once: true, passive: true });
    });
}
export function useCameraFaceTracking(enabled) {
    const [pos, setPos] = useState({ x: 0, y: 0 });
    /*
     * AVAAIO VISION CAMERA HEALTH PHASE 4G
     *
     * Regular users receive only a simple safe state:
     * connecting, live or issue. Internal exception details
     * remain in protected developer logs.
     */
    const [cameraStatus, setCameraStatus,] = useState(enabled ? "connecting" : "off");
    const cameraStatusRef = useRef(enabled ? "connecting" : "off");
    const cameraStartAtRef = useRef(0);
    const lastUsableFrameAtRef = useRef(0);
    const lastVideoTimeRef = useRef(-1);
    const updateCameraStatus = (next) => {
        if (cameraStatusRef.current === next) {
            return;
        }
        cameraStatusRef.current = next;
        setCameraStatus(next);
    };
    const streamRef = useRef(null);
    const rafRef = useRef(null);
    const landmarkerRef = useRef(null);
    const smoothRef = useRef({ x: 0, y: 0 });
    /*
     * Phase 4D user-presence signal.
     * React state changes only when face presence changes,
     * avoiding updates during every camera frame.
     */
    const faceDetectedRef = useRef(false);
    const [faceDetected, setFaceDetected,] = useState(false);
    const updateFaceDetected = (detected) => {
        if (faceDetectedRef.current ===
            detected) {
            return;
        }
        faceDetectedRef.current =
            detected;
        setFaceDetected(detected);
    };
    // =====================================================
    // AI Vision refs
    // =====================================================
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    // =====================================================
    // Capture current webcam frame
    // =====================================================
    /*
     * Draw the current camera pixels into the existing shared
     * capture canvas without serializing the frame.
     *
     * Ava's eye uses this direct canvas source. The Vision
     * uploader continues using captureFrame() below.
     */
    const captureCanvasFrame = () => {
        try {
            const video = videoRef.current;
            if (!video) {
                return null;
            }
            const width = video.videoWidth || 0;
            const height = video.videoHeight || 0;
            if (video.readyState < 2 ||
                width < 2 ||
                height < 2) {
                return null;
            }
            let canvas = canvasRef.current;
            if (!canvas) {
                canvas =
                    document.createElement("canvas");
                canvasRef.current =
                    canvas;
            }
            if (canvas.width !== width ||
                canvas.height !== height) {
                canvas.width =
                    width;
                canvas.height =
                    height;
            }
            const context = canvas.getContext("2d", {
                alpha: false,
            });
            if (!context) {
                return null;
            }
            context.setTransform(1, 0, 0, 1, 0, 0);
            context.drawImage(video, 0, 0, width, height);
            return canvas;
        }
        catch (error) {
            console.warn("[FaceTrack] captureCanvasFrame failed:", error);
            return null;
        }
    };
    const captureFrame = () => {
        try {
            const canvas = captureCanvasFrame();
            if (!canvas) {
                return null;
            }
            return canvas.toDataURL("image/jpeg", 0.72);
        }
        catch (error) {
            console.warn("[FaceTrack] captureFrame failed:", error);
            return null;
        }
    };
    useEffect(() => {
        let alive = true;
        let frameHealthInterval = null;
        cameraStartAtRef.current =
            performance.now();
        lastUsableFrameAtRef.current = 0;
        lastVideoTimeRef.current = -1;
        updateCameraStatus(enabled ? "connecting" : "off");
        const debugCam = (() => {
            try {
                return localStorage.getItem("ava_cam_debug") === "1";
            }
            catch {
                return false;
            }
        })();
        const cleanup = () => {
            if (frameHealthInterval !== null) {
                window.clearInterval(frameHealthInterval);
                frameHealthInterval = null;
            }
            try {
                if (rafRef.current)
                    cancelAnimationFrame(rafRef.current);
            }
            catch { }
            rafRef.current = null;
            try {
                if (streamRef.current)
                    streamRef.current.getTracks().forEach((t) => t.stop());
            }
            catch { }
            streamRef.current = null;
            try {
                if (landmarkerRef.current?.close)
                    landmarkerRef.current.close();
            }
            catch { }
            landmarkerRef.current = null;
        };
        if (!enabled) {
            cleanup();
            updateCameraStatus("off");
            updateFaceDetected(false);
            smoothRef.current = { x: 0, y: 0 };
            setPos({ x: 0, y: 0 });
            return () => {
                alive = false;
                cleanup();
            };
        }
        /*
         * Detect ended streams and stalled camera frames.
         * A permission dialog may remain open without being
         * treated as an issue because no stream exists yet.
         */
        frameHealthInterval =
            window.setInterval(() => {
                if (!alive || !enabled) {
                    return;
                }
                const tracks = streamRef.current
                    ?.getVideoTracks() || [];
                if (tracks.length === 0) {
                    return;
                }
                const liveTrack = tracks.some(track => track.readyState === "live");
                if (!liveTrack) {
                    updateCameraStatus("issue");
                    updateFaceDetected(false);
                    return;
                }
                const referenceTime = lastUsableFrameAtRef.current > 0
                    ? lastUsableFrameAtRef.current
                    : cameraStartAtRef.current;
                if (performance.now() -
                    referenceTime >
                    5500) {
                    updateCameraStatus("issue");
                    updateFaceDetected(false);
                }
            }, 1000);
        let video = document.createElement("video");
        // expose live webcam stream to AI Vision layer
        videoRef.current = video;
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute("playsinline", "true");
        video.setAttribute("webkit-playsinline", "true");
        video.setAttribute("muted", "true");
        video.setAttribute("aria-hidden", "true");
        video.disablePictureInPicture =
            true;
        /*
         * Do not use display:none here.
         *
         * Safari/WKWebView can suspend decoding or return black
         * canvas frames when a live video is removed from layout.
         * Keep the internal camera element rendered offscreen.
         */
        Object.assign(video.style, {
            position: "fixed",
            left: "-10000px",
            top: "0",
            width: "2px",
            height: "2px",
            opacity: "0.001",
            pointerEvents: "none",
            zIndex: "-1",
            objectFit: "cover",
        });
        document.body.appendChild(video);
        const start = async () => {
            try {
                if (!alive)
                    return;
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: "user",
                        width: { ideal: 640 },
                        height: { ideal: 480 },
                    },
                    audio: false,
                });
                if (!alive) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                streamRef.current = stream;
                cameraStartAtRef.current =
                    performance.now();
                lastUsableFrameAtRef.current = 0;
                lastVideoTimeRef.current = -1;
                updateCameraStatus("connecting");
                if (video) {
                    video.srcObject = stream;
                    /*
                     * Safari may accept play() before metadata is ready
                     * but still expose a zero-sized black video frame.
                     */
                    await new Promise((resolve) => {
                        if (!video ||
                            video.readyState >= 1) {
                            resolve();
                            return;
                        }
                        let settled = false;
                        const finish = () => {
                            if (settled)
                                return;
                            settled = true;
                            video?.removeEventListener("loadedmetadata", finish);
                            video?.removeEventListener("canplay", finish);
                            resolve();
                        };
                        video.addEventListener("loadedmetadata", finish, { once: true });
                        video.addEventListener("canplay", finish, { once: true });
                        window.setTimeout(finish, 1800);
                    });
                    try {
                        await video.play();
                    }
                    catch (e) {
                        console.warn("[FaceTrack] video.play() blocked/failed:", e);
                    }
                }
                const fileset = await FilesetResolver.forVisionTasks(WASM_BASE_URL);
                if (!alive)
                    return;
                let landmarker;
                try {
                    landmarker = await FaceLandmarker.createFromOptions(fileset, {
                        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
                        runningMode: "VIDEO",
                        numFaces: 1,
                    });
                }
                catch (e) {
                    console.warn("[FaceTrack] GPU delegate failed, falling back to CPU:", e);
                    landmarker = await FaceLandmarker.createFromOptions(fileset, {
                        baseOptions: { modelAssetPath: MODEL_URL, delegate: "CPU" },
                        runningMode: "VIDEO",
                        numFaces: 1,
                    });
                }
                landmarkerRef.current = landmarker;
                const loop = () => {
                    if (!alive)
                        return;
                    const v = video;
                    const lm = landmarkerRef.current;
                    if (!v || !lm) {
                        rafRef.current = requestAnimationFrame(loop);
                        return;
                    }
                    const w = v.videoWidth || 0;
                    const h = v.videoHeight || 0;
                    const videoTime = Number(v.currentTime || 0);
                    if (v.readyState >= 2 &&
                        w >= 2 &&
                        h >= 2 &&
                        videoTime >
                            lastVideoTimeRef.current +
                                0.001) {
                        lastVideoTimeRef.current =
                            videoTime;
                        lastUsableFrameAtRef.current =
                            performance.now();
                        updateCameraStatus("live");
                    }
                    if (w < 2 || h < 2) {
                        rafRef.current = requestAnimationFrame(loop);
                        return;
                    }
                    try {
                        const nowMs = performance.now();
                        const result = lm.detectForVideo(v, nowMs);
                        // __AVAAIO_MEDIAPIPE_FACE_EVIDENCE_V1__
                        const face = result?.faceLandmarks?.[0];
                        const capturedAt = Date.now();
                        let faceEvidence = {
                            detected: false,
                            frame_width: w,
                            frame_height: h,
                            captured_at: capturedAt / 1000,
                            source: "mediapipe_face_landmarker",
                        };
                        if (face && face.length) {
                            const validPoints = face.filter((point) => Number.isFinite(point?.x) &&
                                Number.isFinite(point?.y) &&
                                point.x >= 0 &&
                                point.x <= 1 &&
                                point.y >= 0 &&
                                point.y <= 1);
                            if (validPoints.length >= 20) {
                                const xs = validPoints.map((point) => point.x);
                                const ys = validPoints.map((point) => point.y);
                                const rawX1 = Math.min(...xs);
                                const rawY1 = Math.min(...ys);
                                const rawX2 = Math.max(...xs);
                                const rawY2 = Math.max(...ys);
                                const rawWidth = rawX2 - rawX1;
                                const rawHeight = rawY2 - rawY1;
                                const padX = Math.max(0.01, rawWidth * 0.12);
                                const padY = Math.max(0.01, rawHeight * 0.12);
                                const x1 = clamp(rawX1 - padX, 0, 1);
                                const y1 = clamp(rawY1 - padY, 0, 1);
                                const x2 = clamp(rawX2 + padX, 0, 1);
                                const y2 = clamp(rawY2 + padY, 0, 1);
                                if (x2 - x1 >= 0.02 &&
                                    y2 - y1 >= 0.02) {
                                    faceEvidence = {
                                        detected: true,
                                        box: {
                                            x1,
                                            y1,
                                            x2,
                                            y2,
                                        },
                                        frame_width: w,
                                        frame_height: h,
                                        captured_at: capturedAt / 1000,
                                        source: "mediapipe_face_landmarker",
                                    };
                                }
                            }
                            updateFaceDetected(true);
                            const p = face[1] || face[0];
                            const nx = (p.x - 0.5) * 2;
                            const ny = (0.5 - p.y) * 2;
                            const s = smoothRef.current;
                            /*
                             * Phase 4B: faster Vision tracking.
                             * Retain enough smoothing to prevent camera jitter.
                             */
                            s.x =
                                s.x * 0.52 +
                                    nx * 0.48;
                            s.y =
                                s.y * 0.52 +
                                    ny * 0.48;
                            const out = {
                                x: clamp(s.x, -1, 1),
                                y: clamp(s.y, -1, 1),
                            };
                            setPos(out);
                            if (debugCam) {
                                console.log("[FaceTrack] pos =", out);
                            }
                        }
                        else {
                            updateFaceDetected(false);
                            const s = smoothRef.current;
                            /*
                             * Preserve the last known direction briefly.
                             * AvaModel performs the deliberate center return
                             * and searching motion.
                             */
                            s.x *= 0.97;
                            s.y *= 0.97;
                            const out = {
                                x: s.x,
                                y: s.y,
                            };
                            setPos(out);
                            if (debugCam) {
                                console.log("[FaceTrack] no face, pos =", out);
                            }
                        }
                        // Upload only after the MediaPipe result for
                        // this camera cycle has been calculated.
                        if (capturedAt - lastVisionUpload >
                            VISION_UPLOAD_INTERVAL) {
                            lastVisionUpload =
                                capturedAt;
                            sendFrameToVision(captureFrame, faceEvidence).catch(console.warn);
                        }
                    }
                    catch (e) {
                        if (debugCam)
                            console.warn("[FaceTrack] detectForVideo error:", e);
                    }
                    rafRef.current = requestAnimationFrame(loop);
                };
                rafRef.current = requestAnimationFrame(loop);
            }
            catch (e) {
                console.error("[FaceTrack] start() failed:", e);
                updateCameraStatus("issue");
                updateFaceDetected(false);
                smoothRef.current = { x: 0, y: 0 };
                if (alive) {
                    setPos({ x: 0, y: 0 });
                }
            }
        };
        start();
        return () => {
            alive = false;
            cleanup();
            try {
                if (video) {
                    try {
                        video.pause();
                        video.srcObject = null;
                    }
                    catch { }
                    if (videoRef.current === video) {
                        videoRef.current = null;
                    }
                    video.remove();
                }
            }
            catch { }
            video = null;
        };
    }, [enabled]);
    return {
        pos,
        faceDetected,
        cameraStatus,
        captureFrame,
        captureCanvasFrame,
        videoRef,
    };
}
